import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import clsx from 'clsx';
import { api } from '../../shared/api';
import { fmtDate, fmtTime, todayLocalIso } from '../../shared/dates';
import { LatePadModal } from './LatePadModal';
import { ServiceLabels } from '@census/shared';
import { Icon } from '../../ui/Icon';

type Tab = 'groups' | 'individual' | 'unfilled';

function ReasonModal({
  title,
  onConfirm,
  onCancel,
}: {
  title: string;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-base font-semibold text-ink">{title}</h2>
        <input
          ref={inputRef}
          autoFocus
          className="input w-full"
          placeholder="Enter reason…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onConfirm(reason || 'Unspecified');
            if (e.key === 'Escape') onCancel();
          }}
        />
        <div className="mt-4 flex justify-end gap-2">
          <button className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn-primary" onClick={() => onConfirm(reason || 'Unspecified')}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

export function AttendanceTodayPage() {
  const queryClient = useQueryClient();
  const [date, setDate] = useState<string>(todayLocalIso());
  const [tab, setTab] = useState<Tab>('groups');
  const [latePadOpen, setLatePadOpen] = useState<{ id: string; name: string } | null>(null);
  const [reasonModal, setReasonModal] = useState<{
    title: string;
    onConfirm: (reason: string) => void;
  } | null>(null);
  const [search, setSearch] = useState<string>('');
  // Focus filters — a facilitator opens the page and picks just their group
  // (or provider, or service kind) to mark attendance in the first 5 minutes
  // of class. Empty string = "all".
  const [groupFilter, setGroupFilter] = useState<string>('');
  const [providerFilter, setProviderFilter] = useState<string>('');
  const [serviceFilter, setServiceFilter] = useState<string>('');

  const { data, refetch } = useQuery({
    queryKey: ['daily-sheet', date],
    queryFn: () => api.get('/instances/daily-sheet', { params: { date } }).then((r) => r.data),
  });

  const checkIn = useMutation({
    mutationFn: ({ id, lateMinutes }: { id: string; lateMinutes?: number }) =>
      api.post(`/instances/${id}/check-in`, { lateMinutes }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily-sheet'] });
      toast.success('Checked in');
    },
  });
  const noShow = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/instances/${id}/no-show`, { reason }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily-sheet'] });
      toast.success('Marked no-show');
    },
  });
  const cancel = useMutation({
    mutationFn: ({ id, by, reason }: { id: string; by: 'CLIENT' | 'PROVIDER' | 'CLINIC'; reason: string }) =>
      api.post(`/instances/${id}/cancel`, { by, reason }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily-sheet'] });
      toast.success('Cancelled');
    },
  });
  const bulkCheckIn = useMutation({
    mutationFn: (ids: string[]) =>
      api.post('/instances/bulk-check-in', { ids }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily-sheet'] });
      toast.success('Bulk check-in complete');
    },
  });

  const filteredGroups = useMemo(() => {
    if (!data) return [];
    const term = search.toLowerCase();
    return data.groups
      .filter((g: any) => !groupFilter || g.resourceId === groupFilter)
      .filter((g: any) => !serviceFilter || g.groupServiceType === serviceFilter)
      .map((g: any) => ({
        ...g,
        rows: g.rows.filter(
          (r: any) =>
            !term ||
            r.name?.toLowerCase().includes(term) ||
            (r.cohortId?.toLowerCase().includes(term) ?? false),
        ),
      }));
  }, [data, search, groupFilter, serviceFilter]);

  const filteredIndividual = useMemo(() => {
    if (!data) return [];
    const term = search.toLowerCase();
    return data.individual
      .filter((i: any) => !providerFilter || i.resourceName === providerFilter)
      .filter((i: any) => !serviceFilter || i.serviceType === serviceFilter)
      .filter(
        (i: any) =>
          !term ||
          i.name?.toLowerCase().includes(term) ||
          (i.cohortId?.toLowerCase().includes(term) ?? false),
      );
  }, [data, search, providerFilter, serviceFilter]);

  // Dropdown options derived from the day's data — no extra API calls needed,
  // and they show only what's actually on the schedule today.
  const groupOptions = useMemo(() => {
    if (!data) return [] as Array<{ id: string; label: string }>;
    return (data.groups as any[]).map((g) => ({
      id: g.resourceId,
      label: `${g.displayName} (${fmtTime(g.startMin)}–${fmtTime(g.endMin)})`,
    }));
  }, [data]);
  const providerOptions = useMemo(() => {
    if (!data) return [] as string[];
    return Array.from(
      new Set((data.individual as any[]).map((i) => i.resourceName).filter(Boolean)),
    ).sort();
  }, [data]);
  const serviceOptions = useMemo(() => {
    if (!data) return [] as string[];
    const fromGroups = (data.groups as any[]).map((g) => g.groupServiceType).filter(Boolean);
    const fromIndividual = (data.individual as any[]).map((i) => i.serviceType);
    return Array.from(new Set([...fromGroups, ...fromIndividual])).sort();
  }, [data]);
  const anyFilterActive = !!(groupFilter || providerFilter || serviceFilter);

  const unfilledGroups = filteredGroups
    .map((g: any) => ({ ...g, rows: g.rows.filter((r: any) => r.status === 'SCHEDULED') }))
    .filter((g: any) => g.rows.length > 0);
  const unfilledIndividual = filteredIndividual.filter((i: any) => i.status === 'SCHEDULED');

  const unfilledCount =
    unfilledGroups.reduce((s: number, g: any) => s + g.rows.length, 0) + unfilledIndividual.length;

  return (
    <div className="space-y-4">
      {/* Page header */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">Attendance</h1>
          <p className="text-sm text-ink-subtle">
            {fmtDate(date)} · tap a card to mark attendance
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            className="input h-9 w-auto py-1.5"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <button
            className="btn-secondary h-9 whitespace-nowrap"
            onClick={() => window.open(`/attendance/sheet?date=${date}`, '_blank')}
            title="Open printable daily attendance sheet"
          >
            <Icon name="print" size={14} /> Print sheet
          </button>
          <button
            className="btn-ghost h-9 whitespace-nowrap"
            onClick={() => refetch()}
            title="Refresh data"
          >
            <Icon name="check-square" size={14} /> Refresh
          </button>
        </div>
      </header>

      {/* Stats strip */}
      {data && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
          <Stat label="Expected" value={data.totals.expected} tone="info" />
          <Stat label="Attended" value={data.totals.attended} tone="success" />
          <Stat label="No-show" value={data.totals.noShow} tone="danger" />
          <Stat label="Cancelled" value={data.totals.cancelled} tone="muted" />
        </div>
      )}

      {/* Search */}
      <div className="card flex items-center gap-2 px-3 py-2">
        <span className="text-ink-subtle">
          <Icon name="users" size={16} />
        </span>
        <input
          className="w-full border-0 bg-transparent text-sm text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-0"
          placeholder="Search by cohort ID or name (e.g., C7 or Doe)…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="grid h-6 w-6 place-items-center rounded-md text-ink-subtle hover:bg-surface-muted"
            aria-label="Clear search"
          >
            <Icon name="x" size={14} />
          </button>
        )}
      </div>

      {/* Focus filters — pick just your group / provider / service to mark
          attendance without scrolling the whole day. */}
      <div className="card flex flex-wrap items-center gap-2 px-3 py-2">
        <span className="text-2xs font-semibold uppercase tracking-wide text-ink-subtle">
          Focus:
        </span>
        <select
          className="input h-8 w-auto py-1 text-xs"
          value={groupFilter}
          onChange={(e) => setGroupFilter(e.target.value)}
        >
          <option value="">All groups</option>
          {groupOptions.map((g) => (
            <option key={g.id} value={g.id}>
              {g.label}
            </option>
          ))}
        </select>
        <select
          className="input h-8 w-auto py-1 text-xs"
          value={providerFilter}
          onChange={(e) => setProviderFilter(e.target.value)}
        >
          <option value="">All providers</option>
          {providerOptions.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <select
          className="input h-8 w-auto py-1 text-xs"
          value={serviceFilter}
          onChange={(e) => setServiceFilter(e.target.value)}
        >
          <option value="">All services</option>
          {serviceOptions.map((s) => (
            <option key={s} value={s}>
              {ServiceLabels[s as 'PHP'] ?? s}
            </option>
          ))}
        </select>
        {anyFilterActive && (
          <button
            className="btn-ghost h-8 px-2 text-xs"
            onClick={() => {
              setGroupFilter('');
              setProviderFilter('');
              setServiceFilter('');
            }}
          >
            Clear filters
          </button>
        )}
        <span className="ml-auto hidden text-2xs text-ink-subtle sm:inline">
          Tip: facilitators can bookmark a URL or pick their group here to skip
          the full-day scroll.
        </span>
      </div>

      {/* Tabs */}
      <div className="flex w-full overflow-hidden rounded-card bg-surface-muted p-0.5 ring-1 ring-line">
        {[
          { v: 'groups' as const, label: 'Groups', count: data?.groups?.length ?? 0 },
          { v: 'individual' as const, label: 'Individual', count: data?.individual?.length ?? 0 },
          { v: 'unfilled' as const, label: 'Unfilled', count: unfilledCount, tone: unfilledCount > 0 ? 'warn' : undefined },
        ].map((t) => (
          <button
            key={t.v}
            className={clsx(
              'flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition',
              tab === t.v
                ? 'bg-white text-ink shadow-sm'
                : 'text-ink-muted hover:text-ink',
            )}
            onClick={() => setTab(t.v)}
          >
            <span>{t.label}</span>
            <span
              className={clsx(
                'rounded px-1.5 py-0.5 text-2xs font-semibold tabular-nums',
                tab === t.v
                  ? 'bg-brand-100 text-brand-700'
                  : t.tone === 'warn'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-surface-muted text-ink-subtle',
              )}
            >
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {tab === 'groups' && (
        <div className="space-y-4">
          {filteredGroups.map((g: any) => (
            <GroupRoster
              key={g.resourceId}
              group={g}
              onCheckIn={(id) => checkIn.mutate({ id })}
              onLate={(id, name) => setLatePadOpen({ id, name })}
              onNoShow={(id) => setReasonModal({ title: 'No-show reason?', onConfirm: (reason) => { noShow.mutate({ id, reason }); setReasonModal(null); } })}
              onCancel={(id) => setReasonModal({ title: 'Cancellation reason?', onConfirm: (reason) => { cancel.mutate({ id, by: 'CLIENT', reason }); setReasonModal(null); } })}
              onBulkCheckIn={(ids) => bulkCheckIn.mutate(ids)}
            />
          ))}
        </div>
      )}

      {tab === 'individual' && (
        <IndividualList
          items={filteredIndividual}
          onCheckIn={(id) => checkIn.mutate({ id })}
          onLate={(id, name) => setLatePadOpen({ id, name })}
          onNoShow={(id) => setReasonModal({ title: 'No-show reason?', onConfirm: (reason) => { noShow.mutate({ id, reason }); setReasonModal(null); } })}
          onCancel={(id) => setReasonModal({ title: 'Cancellation reason?', onConfirm: (reason) => { cancel.mutate({ id, by: 'CLIENT', reason }); setReasonModal(null); } })}
        />
      )}

      {tab === 'unfilled' && (
        <div className="space-y-4">
          {unfilledGroups.map((g: any) => (
            <GroupRoster
              key={g.resourceId}
              group={g}
              onCheckIn={(id) => checkIn.mutate({ id })}
              onLate={(id, name) => setLatePadOpen({ id, name })}
              onNoShow={(id) => setReasonModal({ title: 'No-show reason?', onConfirm: (reason) => { noShow.mutate({ id, reason }); setReasonModal(null); } })}
              onCancel={(id) => setReasonModal({ title: 'Cancellation reason?', onConfirm: (reason) => { cancel.mutate({ id, by: 'CLIENT', reason }); setReasonModal(null); } })}
              onBulkCheckIn={(ids) => bulkCheckIn.mutate(ids)}
            />
          ))}
          <IndividualList
            items={unfilledIndividual}
            onCheckIn={(id) => checkIn.mutate({ id })}
            onLate={(id, name) => setLatePadOpen({ id, name })}
            onNoShow={(id) => setReasonModal({ title: 'No-show reason?', onConfirm: (reason) => { noShow.mutate({ id, reason }); setReasonModal(null); } })}
            onCancel={(id) => setReasonModal({ title: 'Cancellation reason?', onConfirm: (reason) => { cancel.mutate({ id, by: 'CLIENT', reason }); setReasonModal(null); } })}
          />
        </div>
      )}

      {latePadOpen && (
        <LatePadModal
          name={latePadOpen.name}
          onCancel={() => setLatePadOpen(null)}
          onSubmit={(minutes) => {
            checkIn.mutate({ id: latePadOpen.id, lateMinutes: minutes });
            setLatePadOpen(null);
          }}
        />
      )}

      {reasonModal && (
        <ReasonModal
          title={reasonModal.title}
          onConfirm={reasonModal.onConfirm}
          onCancel={() => setReasonModal(null)}
        />
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'success' | 'danger' | 'muted' | 'info';
}) {
  const cls =
    tone === 'success'
      ? 'bg-emerald-50 text-emerald-800 ring-emerald-200'
      : tone === 'danger'
        ? 'bg-rose-50 text-rose-800 ring-rose-200'
        : tone === 'muted'
          ? 'bg-surface-muted text-ink-muted ring-line'
          : 'bg-brand-50 text-brand-800 ring-brand-200';
  return (
    <div
      className={clsx(
        'flex flex-col items-start gap-0.5 rounded-card px-4 py-3 ring-1 ring-inset',
        cls,
      )}
    >
      <span className="text-2xl font-semibold leading-tight tabular-nums">{value}</span>
      <span className="text-2xs font-medium uppercase tracking-wider opacity-80">{label}</span>
    </div>
  );
}

function GroupRoster({
  group,
  onCheckIn,
  onLate,
  onNoShow,
  onCancel,
  onBulkCheckIn,
}: {
  group: any;
  onCheckIn: (id: string) => void;
  onLate: (id: string, name: string) => void;
  onNoShow: (id: string) => void;
  onCancel: (id: string) => void;
  onBulkCheckIn: (ids: string[]) => void;
}) {
  const scheduledIds = group.rows.filter((r: any) => r.status === 'SCHEDULED' && r.id).map((r: any) => r.id);
  return (
    <section className="card overflow-hidden">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3">
        <div>
          <h2 className="text-base font-semibold">{group.displayName}</h2>
          <p className="text-xs text-slate-500">
            {fmtTime(group.startMin)}–{fmtTime(group.endMin)} · {group.rows.length} enrolled
            {group.roomName ? ` · 📍 ${group.roomName}` : ''}
          </p>
        </div>
        {scheduledIds.length > 0 && (
          <button
            className="btn-primary btn-touch"
            onClick={() => onBulkCheckIn(scheduledIds)}
          >
            Mark all attended ({scheduledIds.length})
          </button>
        )}
      </header>
      <ul className="divide-y divide-slate-100">
        {group.rows.map((r: any) => (
          <li key={r.clientId} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <div className="min-w-[140px]">
              <div className="text-lg font-semibold text-brand-700">{r.cohortId}</div>
              <div className="text-sm text-slate-700">{r.name}</div>
            </div>
            <StatusPill status={r.status} />
            {r.id && r.status === 'SCHEDULED' ? (
              <div className="ml-auto flex flex-wrap gap-2">
                <button className="btn-primary btn-touch" onClick={() => onCheckIn(r.id)}>
                  ✓ Attended
                </button>
                <button className="btn-secondary btn-touch" onClick={() => onLate(r.id, r.name)}>
                  ⏱ Late
                </button>
                <button className="btn-danger btn-touch" onClick={() => onNoShow(r.id)}>
                  ✗ No-show
                </button>
                <button className="btn-secondary btn-touch" onClick={() => onCancel(r.id)}>
                  Cancel
                </button>
              </div>
            ) : (
              <div className="ml-auto text-xs text-slate-500">
                {r.lateMinutes ? `Late ${r.lateMinutes} min · ` : ''}
                {r.noShowReason || r.cancellationReason || ''}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function IndividualList({
  items,
  onCheckIn,
  onLate,
  onNoShow,
  onCancel,
}: {
  items: any[];
  onCheckIn: (id: string) => void;
  onLate: (id: string, name: string) => void;
  onNoShow: (id: string) => void;
  onCancel: (id: string) => void;
}) {
  if (items.length === 0)
    return (
      <div className="card p-6 text-center text-sm text-slate-500">
        No individual appointments here.
      </div>
    );
  return (
    <ul className="grid gap-3 md:grid-cols-2">
      {items.map((i) => (
        <li key={i.id} className="card flex flex-col gap-2 p-4">
          <header className="flex items-start justify-between gap-2">
            <div>
              <div className="text-lg font-semibold text-brand-700">{i.cohortId}</div>
              <div className="text-sm font-medium">{i.name}</div>
              <div className="text-xs text-slate-500">
                {fmtTime(i.startMin)}–{fmtTime(i.endMin)} ·{' '}
                {ServiceLabels[i.serviceType as 'THERAPY'] ?? i.serviceType}
                {i.resourceName ? ` · ${i.resourceName}` : ''}
                {i.viaTelehealth ? ' · 📹 Telehealth' : ''}
              </div>
            </div>
            <StatusPill status={i.status} />
          </header>
          {i.status === 'SCHEDULED' ? (
            <div className="flex flex-wrap gap-2">
              <button className="btn-primary btn-touch flex-1" onClick={() => onCheckIn(i.id)}>
                ✓ Attended
              </button>
              <button className="btn-secondary btn-touch" onClick={() => onLate(i.id, i.name)}>
                ⏱ Late
              </button>
              <button className="btn-danger btn-touch" onClick={() => onNoShow(i.id)}>
                ✗ No-show
              </button>
              <button className="btn-secondary btn-touch" onClick={() => onCancel(i.id)}>
                Cancel
              </button>
            </div>
          ) : (
            <p className="text-xs text-slate-500">
              {i.lateMinutes ? `Late ${i.lateMinutes} min · ` : ''}
              {i.noShowReason || i.cancellationReason || ''}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

function StatusPill({ status }: { status: string }) {
  const cls =
    status === 'ATTENDED' || status === 'LATE'
      ? 'pill-success'
      : status === 'NO_SHOW'
        ? 'pill-danger'
        : status === 'SCHEDULED'
          ? 'pill-info'
          : 'pill-muted';
  return <span className={cls}>{status}</span>;
}
